import React, { useState, useEffect } from 'react';
import { useSiteData } from '@/context/SiteDataContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, dealTitle, destination }) => {
  const [formData, setFormData] = useState({
    travelFrom: '',
    travelTo: destination || '',
    specificDestination: dealTitle || '',
    startDate: '',
    endDate: '',
    adults: 1,
    children: 0
  });
  const [numDays, setNumDays] = useState('');
  const [daysError, setDaysError] = useState(false);
  const { siteSettings } = useSiteData();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Pre-fill destination if provided
      setFormData(prev => ({
        ...prev,
        travelTo: destination || prev.travelTo,
        specificDestination: dealTitle || prev.specificDestination
      }));
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, destination, dealTitle]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateDays = () => {
    const { startDate, endDate } = formData;
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (end > start) {
        const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        setNumDays(`${diff} Day${diff > 1 ? 's' : ''}`);
        setDaysError(false);
      } else {
        setNumDays("Check-out must be after check-in");
        setDaysError(true);
      }
    }
  };

  useEffect(() => {
    calculateDays();
  }, [formData.startDate, formData.endDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Submit to backend
    try {
      await fetch(`${API_URL}/api/booking-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          adults: parseInt(formData.adults),
          children: parseInt(formData.children),
          dealTitle: dealTitle || null
        })
      });
    } catch (err) {
      console.error('Error submitting booking inquiry:', err);
    }
    
    const message = `Hi, I'm planning an international family trip.

Travel From: ${formData.travelFrom}
Travel To: ${formData.travelTo}
Destination: ${formData.specificDestination}
Start Date: ${formData.startDate}
End Date: ${formData.endDate}
Number of Days: ${numDays}
Adults: ${formData.adults}
Children: ${formData.children}

Please help me plan this trip.`;

    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = siteSettings.whatsappNumber.replace('+', '');
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="booking-modal-overlay"
      onClick={handleBackdropClick}
      data-testid="booking-modal-overlay"
    >
      <div className="booking-modal-container" data-testid="booking-modal">
        {/* Header */}
        <div className="booking-modal-header">
          <div>
            <h3 className="booking-modal-title">Plan Your Trip</h3>
            <p className="booking-modal-subtitle">Submit the form to chat with our agent</p>
          </div>
          <button 
            onClick={onClose} 
            className="booking-modal-close-btn"
            data-testid="booking-modal-close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="booking-modal-form" data-testid="booking-form">
          <div className="booking-form-row">
            <div className="booking-form-field">
              <label className="booking-form-label">Travel From</label>
              <input 
                type="text" 
                name="travelFrom"
                value={formData.travelFrom}
                onChange={handleChange}
                placeholder="Origin City" 
                required 
                className="booking-form-input"
                data-testid="booking-travel-from"
              />
            </div>
            <div className="booking-form-field">
              <label className="booking-form-label">Travel To</label>
              <input 
                type="text" 
                name="travelTo"
                value={formData.travelTo}
                onChange={handleChange}
                placeholder="Destination City" 
                required 
                className="booking-form-input"
                data-testid="booking-travel-to"
              />
            </div>
          </div>

          <div className="booking-form-field">
            <label className="booking-form-label">Specific Resort/Destination</label>
            <input 
              type="text" 
              name="specificDestination"
              value={formData.specificDestination}
              onChange={handleChange}
              placeholder="E.g. Soneva Jani Resort" 
              required 
              className="booking-form-input"
              data-testid="booking-destination"
            />
          </div>

          <div className="booking-form-row">
            <div className="booking-form-field">
              <label className="booking-form-label">Start Date</label>
              <input 
                type="date" 
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required 
                className="booking-form-input"
                data-testid="booking-start-date"
              />
            </div>
            <div className="booking-form-field">
              <label className="booking-form-label">End Date</label>
              <input 
                type="date" 
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required 
                className="booking-form-input"
                data-testid="booking-end-date"
              />
            </div>
          </div>

          <div className="booking-form-field">
            <label className="booking-form-label">Number of Days</label>
            <input 
              type="text" 
              value={numDays}
              readOnly
              placeholder="Pick dates to calculate" 
              className={`booking-form-input booking-days-input ${daysError ? 'error' : ''}`}
              data-testid="booking-num-days"
            />
          </div>

          <div className="booking-form-row">
            <div className="booking-form-field">
              <label className="booking-form-label">Adults (12+)</label>
              <input 
                type="number" 
                name="adults"
                value={formData.adults}
                onChange={handleChange}
                min="1"
                required 
                className="booking-form-input"
                data-testid="booking-adults"
              />
            </div>
            <div className="booking-form-field">
              <label className="booking-form-label">Children (0-11)</label>
              <input 
                type="number" 
                name="children"
                value={formData.children}
                onChange={handleChange}
                min="0"
                required 
                className="booking-form-input"
                data-testid="booking-children"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="booking-submit-btn"
            data-testid="booking-submit-btn"
          >
            <span>Send Inquiry to WhatsApp</span>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.588-5.946 0-6.556 5.332-11.891 11.893-11.891 3.181 0 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.481 8.403 0 6.556-5.332 11.89-11.893 11.89-2.015 0-3.991-.511-5.741-1.486l-6.142 1.611-.01-.006zm6.657-3.988l.418.248c1.464.871 3.15 1.33 4.895 1.331 5.398 0 9.787-4.391 9.787-9.792 0-2.612-1.018-5.071-2.866-6.921-1.848-1.85-4.305-2.869-6.92-2.869-5.398 0-9.789 4.391-9.789 9.792 0 1.83.513 3.618 1.481 5.187l.275.441-1.002 3.659 3.721-.976z"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
